import { useApi, SKY_API_ENDPOINT } from '@/api';
import {
  DataTable,
  Card,
  formatCurrency,
  useIsMobile,
  formatPercentage,
  ProtocolIcon,
  Flex,
  Text,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { Link, InfoTooltip, TableSkeleton } from '@/components';

export default function StarsRequiredRiskCapitalTable() {
  const isMobile = useIsMobile();
  const { sorting } = useTableProps({
    initialSorting: '-exposure'
  });

  const [data, error] = useApi({
    baseUrl: SKY_API_ENDPOINT,
    url: `/rrc/primes/`,
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={3} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => (
        <Flex gap="3xs" alignItems="center">
          <ProtocolIcon protocol={row.star} size="2" />
          {row.star.toUpperCase()}
        </Flex>
      ),
      columnSize: isMobile ? '70px' : '100px'
    },
    {
      header: (
        <Flex gap="3xs">
          Exposure
          <InfoTooltip text="The Sky Ecosystem's exposure towards a specific Star Agent." />
        </Flex>
      ),
      getCell: (row) => formatCurrency(row.exposure),
      sortingField: 'exposure'
    },
    {
      header: (
        <Flex gap="3xs">
          Total RRC
          <InfoTooltip text="Star Agent specific Total Required Risk Capital." />
        </Flex>
      ),
      getCell: (row) => formatCurrency(row.financial_rrc),
      sortingField: 'financial_rrc'
    },
    {
      header: (
        <Flex gap="3xs">
          % of Exposure
          <InfoTooltip text="Star Agent Specific total Required Risk Capital divided by Total Exposure." />
        </Flex>
      ),
      getCell: (row) => formatPercentage(row.exposure_share),
      sortingField: 'exposure_share'
    },
    {
      header: (
        <Flex gap="3xs">
          Total RC
          <InfoTooltip text="Star Agent specific total Risk Capital." />
        </Flex>
      ),
      getCell: (row) => formatCurrency(row.total_rc),
      sortingField: 'total_rc'
    },
    {
      header: (
        <Flex gap="3xs">
          Encumbrance Ratio
          <InfoTooltip
            text={
              <Text size="3">
                Star Agent specific total Required Risk Capital divided by total
                Risk Capital.
              </Text>
            }
          />
        </Flex>
      ),
      getCell: (row) => formatPercentage(row.risk_tolerance_ratio),
      sortingField: 'risk_tolerance_ratio'
    }
  ];

  return (
    <Card>
      <DataTable
        rows={data.data.results}
        columns={columns}
        sorting={sorting}
        getKey={(row) => row.star}
        rowLinkProps={{
          to: (row) => `/required-risk-capital/${row.star}`,
          // @ts-ignore
          linkComponent: Link
        }}
      />
    </Card>
  );
}
